import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, AreaChart, Legend
} from 'recharts';
import {
  Users, TrendingUp, Activity, MapPin, Briefcase, 
  Languages, Award, Calendar, Network, Star, UserPlus,
  Crown, MessageSquare, Eye
} from 'lucide-react';
import { Member } from '@/types/member';
import { statisticsService } from "@/services/statisticsService";
import { Skeleton } from '@/components/ui/loading-skeleton';
import { toast } from 'sonner';

interface DirectoryAnalyticsProps {
  members: Member[];
}

export const DirectoryAnalytics = ({ members }: DirectoryAnalyticsProps) => {
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [realStats, setRealStats] = useState({
    connections: { totalConnections: 0, totalMessages: 0, activeConversations: 0 },
    members: { totalMembers: 0, activeMembers: 0, premiumMembers: 0, newThisMonth: 0, activeMembersPercentage: 0, premiumMembersPercentage: 0 },
    events: { upcomingEvents: 0, totalRegistrations: 0 },
    growth: { growthData: [] },
    activity: { weeklyActivity: [] },
    skills: { topSkills: [] },
    geography: { topCountries: [] }
  });

  useEffect(() => {
    const fetchAllStats = async () => {
      setIsLoadingStats(true);
      try {
        const [connections, memberStats, events, growth, activity, skills, geography] = await Promise.all([
          statisticsService.getConnectionStats(),
          statisticsService.getDetailedMemberStats(),
          statisticsService.getEventStats(),
          statisticsService.getGrowthEvolution(),
          statisticsService.getActivityStats(),
          statisticsService.getSkillsStats(),
          statisticsService.getGeographicStats()
        ]);
        
        setRealStats({
          connections,
          members: memberStats,
          events,
          growth,
          activity,
          skills,
          geography
        });
      } catch (error) {
        console.error('❌ Failed to load analytics:', error);
        toast.error('Erreur lors du chargement des statistiques', {
          description: 'Certaines données peuvent être incomplètes'
        });
      } finally {
        setIsLoadingStats(false);
      }
    };
    
    // ⏱️ Attendre 500ms pour laisser la session se stabiliser après les optimisations auth
    const timer = setTimeout(() => {
      fetchAllStats();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Analyse des secteurs (tags) - gardé pour compatibilité mais utilisant les données réelles
  const sectorGroups = members.reduce((acc, member) => {
    if (member.tags) {
      member.tags.forEach(tag => {
        acc[tag] = (acc[tag] || 0) + 1;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  const topSectors = Object.entries(sectorGroups)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([sector, count]) => ({ sector, count }));

  if (isLoadingStats) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-20 w-full" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-[300px] w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.members.totalMembers}</div>
              <p className="text-sm text-muted-foreground">Membres totaux</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-full">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.members.activeMembers}</div>
              <p className="text-sm text-muted-foreground">Membres actifs</p>
              <p className="text-xs text-green-600">
                {realStats.members.activeMembersPercentage}% du total
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Crown className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.members.premiumMembers}</div>
              <p className="text-sm text-muted-foreground">Membres Premium</p>
              <p className="text-xs text-purple-600">
                {realStats.members.premiumMembersPercentage}% du total
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <UserPlus className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.members.newThisMonth}</div>
              <p className="text-sm text-muted-foreground">Nouveaux ce mois</p>
              <p className="text-xs text-blue-600">
                {realStats.members.totalMembers > 0 ? Math.round((realStats.members.newThisMonth / realStats.members.totalMembers) * 100) : 0}% du total
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Graphiques de croissance et d'activité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-primary" />
            Évolution des membres (6 derniers mois)
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={realStats.growth.growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="members" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-primary" />
            Activité de la semaine
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={realStats.activity.weeklyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="connections" fill="#8884d8" name="Connexions" />
                <Bar dataKey="messages" fill="#82ca9d" name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Répartition géographique et compétences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-primary" />
            Répartition géographique
          </h3>
          <div className="space-y-4">
            {realStats.geography.topCountries.map((country, index) => (
              <div key={country.country} className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{country.country}</span>
                    <span className="text-sm text-muted-foreground">{country.count}</span>
                  </div>
                  <Progress 
                    value={realStats.members.totalMembers > 0 ? (country.count / realStats.members.totalMembers) * 100 : 0} 
                    className="h-2"
                  />
                </div>
              </div>
            ))}
            {realStats.geography.topCountries.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune donnée géographique disponible</p>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-primary" />
            Compétences les plus recherchées
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart layout="horizontal" data={realStats.skills.topSkills}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="skill" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
            {realStats.skills.topSkills.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune donnée de compétences disponible</p>
            )}
          </div>
        </Card>
      </div>

      {/* Répartition par secteur */}
      {topSectors.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Briefcase className="h-5 w-5 mr-2 text-primary" />
            Répartition par secteur
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topSectors}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                  >
                    {topSectors.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="space-y-3">
              {topSectors.map((sector, index) => (
                <div key={sector.sector} className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                  ></div>
                  <span className="text-sm flex-1">{sector.sector}</span>
                  <Badge variant="outline">{sector.count}</Badge>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Métriques d'engagement réelles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Network className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.connections.totalConnections}</div>
              <p className="text-sm text-muted-foreground">Connexions acceptées</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-cyan-100 rounded-full">
              <MessageSquare className="h-6 w-6 text-cyan-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.connections.totalMessages}</div>
              <p className="text-sm text-muted-foreground">Messages échangés</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-pink-100 rounded-full">
              <Eye className="h-6 w-6 text-pink-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.connections.activeConversations}</div>
              <p className="text-sm text-muted-foreground">Conversations actives</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-indigo-100 rounded-full">
              <Calendar className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{realStats.events.upcomingEvents}</div>
              <p className="text-sm text-muted-foreground">Événements à venir</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};